import React from 'react';
import PropTypes from 'prop-types';
import Layout from '../Layout';
import SEO from '../SEO';
import locales from '../../constants/locales';
import { fillFiguresDeep } from '../../utils/keyFigures';
import {
  Hero, Section, Cards, Steps, Stats, Table, Quote, Todo, CTA, Screens,
  Chips, Tabs, Story, Quotes, Offerings, Logos,
} from '../Sections';

// One block of a section. Block types match the components in src/components/Sections.
const Block = ({ block }) => {
  switch (block.type) {
    case 'cards':
      return <Cards items={block.items} columns={block.columns} />;
    case 'steps':
      return <Steps items={block.items} />;
    case 'stats':
      return <Stats items={block.items} note={block.note} />;
    case 'table':
      return <Table headers={block.headers} rows={block.rows} />;
    case 'screens':
      return <Screens items={block.items} columns={block.columns} />;
    case 'logos':
      return <Logos items={block.items} note={block.note} />;
    case 'chips':
      return <Chips items={block.items} />;
    case 'tabs':
      return <Tabs items={block.items} />;
    case 'story':
      return <Story {...block} />;
    case 'quotes':
      return <Quotes items={block.items} />;
    case 'offerings':
      return <Offerings items={block.items} />;
    case 'quote':
      return <Quote>{block.text}</Quote>;
    case 'todo':
      return <Todo>{block.text}</Todo>;
    case 'text':
      return (
        <div className="corp-text">
          {block.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      );
    default:
      return null;
  }
};

// Data-driven page: content comes from src/data/pages/<page>.js ({ fr, en })
const CorporatePage = ({ locale, path, content, jsonLd }) => {
  const page = fillFiguresDeep(content[locale] || content.fr, locale);
  const url = locales[locale].default ? path : `/${locales[locale].path}${path}`;
  return (
    <Layout locale={locale}>
      <SEO title={page.seo.title} description={page.seo.description} lang={locale} url={url} jsonLd={jsonLd} />
      <div className="landing-page">
        <Hero {...page.hero} />
        {page.sections.map((section) => (
          <Section key={section.id || section.title} {...section}>
            {section.blocks.map((block, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="corp-block"><Block block={block} /></div>
            ))}
          </Section>
        ))}
        {page.cta && <CTA {...page.cta} />}
      </div>
    </Layout>
  );
};

CorporatePage.propTypes = {
  locale: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  content: PropTypes.shape({ fr: PropTypes.object, en: PropTypes.object }).isRequired,
  jsonLd: PropTypes.object,
};

export default CorporatePage;
